import type { ContractItemResponse, UpdateContractItemPayload } from './types';

import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { vestigasApi } from '~/services/kyClient';

import { log } from '~/utils';

import {
  zodContractItemResponseSchema,
  zodUpdateContractItemPayloadSchema,
} from './validations';

/**
 * Updates an existing line item in a contract.
 * This is a partial update - only provided fields are updated.
 *
 * @param contractId - The UUID of the contract containing the item.
 * @param itemId - The UUID of the item to update.
 * @param payload - The contract item fields to update.
 * @returns The updated contract item with all fields.
 * @throws Error if the API request fails. Validation errors are logged but do not throw.
 * @see https://app.dev.vestigas.com/redoc#tag/Contracts/operation/update_contract_item_contracts__contract_id__items__item_id__put
 */
export const updateContractItem = async (
  contractId: UUID,
  itemId: UUID,
  payload: UpdateContractItemPayload,
) => {
  const response = await vestigasApi
    .put(ENDPOINT.CONTRACT_ITEM.UPDATE(contractId, itemId), {
      json: payload,
      validate: {
        body: zodUpdateContractItemPayloadSchema,
      },
    })
    .json<ContractItemResponse>();

  // Validate the response with Zod
  const validatedResponse = zodContractItemResponseSchema.safeParse(response);
  if (!validatedResponse.success && validatedResponse.error.issues.length) {
    log.error({
      message: 'Update contract item API response has validation errors',
      error: {
        errors: validatedResponse.error.issues,
        errorMessage: validatedResponse.error.message,
        response,
      },
    });
  }

  return response;
};
